import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Search, Factory, Trash2, MessageSquare } from 'lucide-react';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { Supplier, SupplierNote } from '../types';

const Talleres: React.FC = () => {
  const { userRole } = useAuth();
  const { 
    suppliers, 
    supplierNotes,
    fetchSuppliers, 
    fetchSupplierNotes,
    addSupplierNote,
    updateSupplierNote,
    deleteSupplierNote
  } = useAppStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [newNote, setNewNote] = useState<Record<string, string>>({});
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState<string>('');
  const [, setIsLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(true);

  // Filtrar proveedores que sean talleres
  const workshopSuppliers = useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) {
      return [];
    }

    const filtered = suppliers.filter(supplier => {
      if (!supplier || !supplier.name) {
        return false;
      }

      const isWorkshop = 
        supplier.category?.toLowerCase() === 'taller' ||
        supplier.name.toLowerCase().includes('taller') ||
        supplier.name.toLowerCase().includes('workshop') ||
        ['alef', 'alex', 'alfredo', 'carlos', 'diego', 'eduardo', 'fernando', 'gabriel', 'hugo', 'ivan', 'juan', 'luis', 'miguel', 'oscar', 'pablo', 'ricardo', 'sergio', 'tomás', 'victor', 'walter'].includes(supplier.name.toLowerCase());
      
      const matchesSearch = supplier.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      return isWorkshop && matchesSearch;
    });
    
    return filtered;
  }, [suppliers, searchQuery]);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      setSuppliersLoading(true);
      setIsLoading(true);
      
      try {
        await Promise.all([
          fetchSuppliers(),
          fetchSupplierNotes()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setSuppliersLoading(false);
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []); // Solo se ejecuta una vez al montar

  // Helper function para obtener notas de un proveedor
  const getSupplierNotes = useCallback((supplierId: string): SupplierNote[] => {
    return supplierNotes.filter((note: SupplierNote) => note.supplier_id === supplierId);
  }, [supplierNotes]);

  const handleAddNote = async (supplierId: string) => {
    const content = newNote[supplierId];
    if (!content?.trim()) return;

    try {
      await addSupplierNote(supplierId, content.trim());
      setNewNote((prev: Record<string, string>) => ({ ...prev, [supplierId]: '' }));
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteSupplierNote(noteId);
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleEditNote = (note: SupplierNote) => {
    setEditingNote(note.id);
    setEditingNoteContent(note.content);
  };

  const handleSaveEdit = async (noteId: string) => {
    try {
      await updateSupplierNote(noteId, editingNoteContent);
      setEditingNote(null);
      setEditingNoteContent('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditingNoteContent('');
  };


  // Verificar permisos
  if (userRole !== 'Administrador' && userRole !== 'Producción') {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Acceso Denegado
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            No tienes permisos para acceder a esta página.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (suppliersLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-300 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Cargando talleres...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Factory className="h-8 w-8" />
            Talleres
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gestión de talleres y control de stock de avíos y materiales
          </p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar talleres..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Lista de Talleres */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {workshopSuppliers.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <Factory className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">
              No hay talleres registrados
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              Los proveedores con categoría "taller" o nombres específicos aparecerán aquí automáticamente.
            </p>
          </div>
        ) : (
          workshopSuppliers.map((supplier: Supplier) => {
            const notes = getSupplierNotes(supplier.id);
            
            return (
              <Card key={supplier.id} className="h-fit border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
                {/* Header del Taller */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <Factory className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {supplier.name}
                        </h3>
                        {supplier.category?.toLowerCase() === 'taller' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            Taller
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            No categorizado
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Información de contacto */}
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {supplier.email && (
                      <p>📧 {supplier.email}</p>
                    )}
                    {supplier.phone && (
                      <p>📞 {supplier.phone}</p>
                    )}
                    {supplier.address && (
                      <p>📍 {supplier.address}</p>
                    )}
                  </div>
                </div>
                
                <CardContent className="p-4">
                  {/* Información del taller */}
                  {supplier.notes && (
                    <div className="mb-6">
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {supplier.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Sección de Notas */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Notas ({notes.length})
                      </h4>
                    </div>
                    
                    {/* Lista de notas */}
                    <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                      {notes.map((note: SupplierNote) => (
                        <div key={note.id} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          {editingNote === note.id ? (
                            <div className="space-y-3">
                              <textarea
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                                rows={3}
                                value={editingNoteContent}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingNoteContent(e.target.value)}
                              />
                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleSaveEdit(note.id)}
                                  disabled={!editingNoteContent.trim()}
                                >
                                  Guardar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                {note.content}
                              </p>
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(note.created_at).toLocaleString('es-AR')}
                                </p>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<Edit className="h-3 w-3" />}
                                    onClick={() => handleEditNote(note)}
                                    className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<Trash2 className="h-3 w-3" />}
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Agregar nueva nota */}
                    <div className="space-y-3">
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                        rows={3}
                        placeholder="Agregar nota sobre stock, avíos, materiales..."
                        value={newNote[supplier.id] || ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewNote((prev: Record<string, string>) => ({ ...prev, [supplier.id]: e.target.value }))}
                      />
                      <div className="flex justify-end">
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Plus className="h-4 w-4" />}
                          onClick={() => handleAddNote(supplier.id)}
                          disabled={!newNote[supplier.id]?.trim()}
                        >
                          Agregar Nota
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Talleres;